import type { OutputData } from '@editorjs/editorjs';
import type {
  ExternalToolSettings,
  ToolConstructable,
} from '@editorjs/editorjs/types/tools';
import type { ReactNode } from 'react';
import type { NewMediaUploadFn } from '../../hooks/api/useMediaUpload';
import type { WebsocketAwarenessState } from '../../hooks/useWebsocket/websocket';
import type { HelpErrorTextsTemplateProps } from '../HelpErrorTextsTemplate/HelpErrorTextsTemplate';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Code from '@editorjs/code';
import Delimiter from '@editorjs/delimiter';
import EditorJS from '@editorjs/editorjs';
import NestedList from '@editorjs/nested-list';
import Paragraph from '@editorjs/paragraph';
import Quote from '@editorjs/quote';
import Table from '@editorjs/table';
import Warning from '@editorjs/warning';
import Header from 'editorjs-header-with-anchor';
import AlignmentTuneTool from 'editorjs-text-alignment-blocktune';
import { twMerge } from 'tailwind-merge';

import { EMPTY_FUNCTION } from '../../lib/globals';
import { getTestProps } from '../../lib/helpers';

import { useModals } from '../../contexts/ModalContext';

import FieldEditorsIndicator from '../FieldEditorsIndicator/FieldEditorsIndicator';
import FileButton from '../FileButton/FileButton';
import HelpErrorTextsTemplate from '../HelpErrorTextsTemplate/HelpErrorTextsTemplate';
import LinkObjectContentModal from '../LinkObjectContentModal/LinkObjectContentModal';
import RequiredTemplate from '../RequiredTemplate/RequiredTemplate';
import FlotiqImagePlugin from './customTools/FlotiqImagePlugin/FlotiqImagePlugin';
import YoutubeEmbed from './customTools/YoutubeEmbed/YoutubeEmbed';

const findTools = (
  editorJsTools: Record<string, ExternalToolSettings>,
  toolsList?: string[],
) => {
  if (!toolsList) return editorJsTools;
  const list: Record<string, ExternalToolSettings> = {
    paragraph: editorJsTools.paragraph,
    alignmentTuneTool: editorJsTools.alignmentTuneTool,
  };
  toolsList.forEach((tool) => {
    if (editorJsTools[tool]) {
      list[tool] = editorJsTools[tool];
    }
  });
  return list;
};

const parseData = (value?: OutputData) => {
  if (value?.blocks?.length) {
    return {
      ...value,
      blocks: value.blocks.map((block) => {
        if (Array.isArray(block.data)) return { ...block, data: {} };
        return block;
      }),
    };
  }
  return value;
};

export interface BlockInputProps extends Omit<
  HelpErrorTextsTemplateProps,
  'additionalHelpClasses'
> {
  /**
   * Field form name
   */
  name?: string;
  /**
   * Blocks value
   */
  value?: OutputData;
  /**
   * On value change callback
   */
  onChange?: (value: OutputData, name?: string) => void;
  /**
   * On blur callback
   */
  onBlur?: (name?: string) => void;
  /**
   * On focus callback
   */
  onFocus?: (name?: string) => void;
  /**
   * List of tools names
   */
  toolsList?: string[];
  /**
   * Label above block input
   */
  label?: ReactNode;
  /**
   * If field is required
   */
  required?: boolean;
  /**
   * If editor is disabled
   */
  disabled?: boolean;
  /**
   * If editor is readonly (non-interactive but without disabled classes)
   */
  readonly?: boolean;
  /**
   * On media upload callback
   */
  onMediaUpload?: NewMediaUploadFn;
  /**
   * Additional container classes
   */
  additionalClasses?: string;
  /**
   * Direction of text
   */
  direction?: 'rtl' | 'ltr';
  /**
   * Editors data who are currently editing the field
   */
  editors?: WebsocketAwarenessState[];
  /**
   * Input test id
   */
  testId?: string;
}

const BlockInput = ({
  name,
  value,
  onChange = EMPTY_FUNCTION,
  onBlur = EMPTY_FUNCTION,
  onFocus,
  toolsList,
  direction = 'ltr',
  label = '',
  required = false,
  disabled = false,
  readonly = false,
  helpText = '',
  error = '',
  onMediaUpload,
  editors,
  additionalClasses = '',
  additionalErrorClasses = '',
  testId = '',
}: BlockInputProps) => {
  const editor = useRef<EditorJS>(undefined);
  const holder = useRef<HTMLDivElement>(null);
  const modal = useModals();
  const { t } = useTranslation();

  const editorReadonly = disabled || readonly;

  const openMediaModal = useCallback(async () => {
    return await modal({
      title: (
        <div className="flex flex-wrap justify-between gap-1">
          <div className="text-3xl">{t('Global.MediaLibrary')}</div>
          <FileButton
            onUpload={onMediaUpload}
            multiple
            additionalClasses="w-fit"
            testId={testId}
          />
        </div>
      ),
      content: (
        <LinkObjectContentModal
          relationType={'_media'}
          onMediaUpload={onMediaUpload}
          returnContentObject
        />
      ),
      size: '2xl',
      dialogAdditionalClasses: 'h-[calc(100vh-32px)]',
    });
  }, [modal, t, onMediaUpload, testId]);

  const editorJsTools = useMemo<Record<string, ExternalToolSettings>>(
    () => ({
      paragraph: {
        class: Paragraph as ToolConstructable,
        inlineToolbar: true,
        tunes: ['alignmentTuneTool'],
      },
      alignmentTuneTool: {
        class: AlignmentTuneTool,
        config: {
          blocks: {
            header: 'center',
          },
        },
      },
      table: { class: Table as ToolConstructable, inlineToolbar: true },
      list: {
        class: NestedList as unknown as ToolConstructable,
        inlineToolbar: true,
      },
      warning: { class: Warning as ToolConstructable, inlineToolbar: true },
      code: { class: Code },
      header: {
        class: Header,
        inlineToolbar: true,
        tunes: ['alignmentTuneTool'],
      },
      quote: { class: Quote, inlineToolbar: true },
      delimiter: { class: Delimiter },
      youtubeEmbed: { class: YoutubeEmbed },
      image: {
        class: FlotiqImagePlugin as ToolConstructable,
        inlineToolbar: true,
        config: {
          openModal: openMediaModal,
        },
      },
    }),
    [openMediaModal],
  );

  const handleSave = useCallback(async () => {
    if (editorReadonly || !editor.current?.save) return;
    const savedData = await editor.current.save();
    onChange(savedData, name);
  }, [editorReadonly, name, onChange]);

  useEffect(() => {
    if (
      editor.current?.destroy &&
      editor.current.readOnly.isEnabled !== editorReadonly
    ) {
      editor.current.destroy();
    }
    if (editor.current && Object.keys(editor.current).length) return;
    editor.current = new EditorJS({
      data: parseData(value),
      readOnly: editorReadonly,
      holder: holder.current ? (holder.current as HTMLDivElement) : undefined,
      tools: findTools(editorJsTools, toolsList),
      onChange: handleSave,
      i18n: {
        direction,
      },
      // There is some issues with importing log level from EditorJS, so we need to manually write the log level
      logLevel: 'ERROR' as any,
    });
    if (holder.current)
      holder.current.addEventListener('focusin', () => {
        onFocus?.(name);
      });
  }, [
    value,
    toolsList,
    handleSave,
    openMediaModal,
    editorJsTools,
    editorReadonly,
    direction,
    onFocus,
    name,
  ]);

  useEffect(() => {
    if (editor.current?.render && editorReadonly && value) {
      editor.current?.render(value);
    }
  }, [editorReadonly, name, value]);

  useEffect(() => {
    if (editorReadonly && (!value || Object.keys(value).length === 0))
      onChange(
        { time: Date.now(), blocks: [], version: EditorJS.version },
        name,
      );
  }, [editorReadonly, name, onChange, value]);

  useEffect(() => {
    if (editor.current && !value?.blocks) {
      editor.current.isReady.then(() => handleSave());
    }
  }, [handleSave, value]);

  useEffect(() => {
    if (!editor.current) return;
    if (editor.current.destroy) {
      editor.current.destroy();
    }
  }, [toolsList]);

  useEffect(() => {
    return () => {
      if (!editor.current) return;
      if (editor.current.destroy) editor.current.destroy();
    };
  }, []);

  const parsedError = useMemo(() => {
    if (
      !!error &&
      typeof error === 'object' &&
      'blocks' in error &&
      error?.blocks
    ) {
      const newErrors = { '': error.blocks, ...error };
      delete newErrors.blocks;
      return newErrors;
    }
    return error;
  }, [error]);

  const handleBlur = useCallback(() => {
    // Due to the align tool does not trigger the change event,
    // we need to trigger on change on blurring
    handleSave();
    onBlur(name);
  }, [handleSave, name, onBlur]);

  return (
    <div className={twMerge('relative', additionalClasses)} onBlur={handleBlur}>
      {label && (
        <label
          className="block text-sm text-slate-400 dark:text-slate-200 mb-1"
          {...getTestProps(testId, 'label')}
        >
          {label}
          {required && <RequiredTemplate />}
        </label>
      )}
      {!label && required && (
        <RequiredTemplate absolute additionalClasses="-right-3 top-2" />
      )}

      <FieldEditorsIndicator editors={editors}>
        <div
          data-field-name={name}
          className={twMerge(
            'border border-slate-200 dark:bg-transparent dark:border-slate-800 rounded-lg w-full dark:text-slate-200',
            disabled ? 'bg-slate-100 cursor-not-allowed' : 'bg-white',
            editorReadonly && 'cursor-not-allowed',
          )}
        >
          <div ref={holder} />
        </div>
      </FieldEditorsIndicator>

      <HelpErrorTextsTemplate
        helpText={helpText}
        error={parsedError}
        additionalErrorClasses={additionalErrorClasses}
        testId={testId}
      />
    </div>
  );
};

export default BlockInput;
